import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsCollection, CmsContent, CmsTaxonomyGeneral } from '@fcom/core-api';
import { CONTEXT, Schema, SchemaService, SchemaTypes } from '@fcom/common/services/schema/schema.service';

const FAQ_SCHEMA_KEY = 'FAQPage:ond';

@Component({
  selector: 'cms-ond-faqs',
  templateUrl: 'cms-ond-faqs.component.html',
  styleUrls: ['cms-ond-faqs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsOndFaqsComponent {
  private readonly schemaService = inject(SchemaService);

  readonly content = input.required<CmsCollection>();

  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly faqs = computed<CmsContent[]>(() => this.content().items ?? []);

  readonly title = computed<string | undefined>(() => {
    const content = this.content();
    const hasNoTitleTag = (content.subjectTaxonomyTags ?? []).includes(CmsTaxonomyGeneral.NO_TITLE);

    return hasNoTitleTag ? undefined : content.teaserTitle;
  });

  readonly schema = computed<Schema | null>(() => {
    const faqs = this.faqs().filter((faq) => {
      return !!faq.teaserTitle && !!faq.teaserText;
    });

    if (!faqs.length) {
      return null;
    }

    return {
      '@context': CONTEXT,
      '@type': SchemaTypes.FAQ_PAGE,
      mainEntity: faqs.map((faq): Schema => {
        return {
          '@type': SchemaTypes.QUESTION,
          name: faq.teaserTitle,
          acceptedAnswer: {
            '@type': SchemaTypes.ANSWER,
            text: faq.teaserText,
          },
        };
      }),
    };
  });

  constructor() {
    effect((onCleanup) => {
      const schema = this.schema();
      if (!schema) {
        return;
      }

      this.schemaService.setSchema(FAQ_SCHEMA_KEY, schema);
      onCleanup(() => {
        this.schemaService.removeSchema(FAQ_SCHEMA_KEY);
      });
    });
  }
}
